import { FC, useMemo, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { formatRate } from './adminBugoStat.util';
import { OrderStatKind } from './adminOrderStat.types';
import { PenetrationBase, PenetrationDrillDown } from './adminPenetration.types';
import {
  aggregatePenetration,
  buildPenetrationRows,
  drillDownPenetrationRows,
  metricsOf,
} from './adminPenetration.util';
import { exportPenetrationExcel } from './adminPenetrationExcel';
import { AdminPenetrationFeventModal } from './AdminPenetrationFeventModal';
import { AdminPenetrationTable } from './AdminPenetrationTable';
import { AnalyticsCalendar } from './AnalyticsCalendar';
import { AnalyticsDailyTrend } from './AnalyticsDailyTrend';
import { CenterLoading, EmptyBox, ErrorBox, NoticeBox, StatCard } from './AnalyticsShared';
import { ORDER_WINDOW_DAYS, useAdminPenetration } from './useAdminPenetration';

//* 발인일 축 뷰. 다른 뷰(표·달력)가 "얼마 팔았나"라면 여기는 "팔 수 있었던 상가 중
//* 몇 %에서 팔았나"를 본다 — 분모가 상가라 매출이 늘어도 구매율은 떨어질 수 있고,
//* 그 차이가 곧 놓친 상가다.

const KIND_LABEL: Record<OrderStatKind, string> = { flower: '화환', gift: '답례품' };

/** 달력 칸에 들어갈 금액 — 칸이 좁아 만/억으로 줄인다 */
const compactWon = (value: number): string =>
  value >= 100_000_000
    ? `${Math.round(value / 10_000_000) / 10}억`
    : value >= 10_000
    ? `${Math.round(value / 1_000) / 10}만`
    : value.toLocaleString();

const CHART_VIEWS = [
  { key: 'calendar' as const, label: '달력' },
  { key: 'trend' as const, label: '추이' },
];

//* 라벨을 '구매율'·'발인 상가'로 두면 요약 카드·표 헤더와 글자가 겹쳐 뭘 누르는지 헷갈린다
const HEAT_METRICS = [
  { key: 'rate' as const, label: '구매율 높은 날', hint: '잘 판 날이 진하게' },
  { key: 'value' as const, label: '상가 많은 날', hint: '상가가 몰린 날이 진하게' },
];

//* 부고를 안 보낸 상가엔 주문이 들어올 길이 아예 없다 — 공유 상가가 진짜 모수라 기본값.
//* 그래도 '공유까지 포함해 얼마나 놓쳤나'를 볼 수 있게 발인 상가 기준도 남긴다.
const BASE_OPTIONS: { key: PenetrationBase; label: string; hint: string }[] = [
  { key: 'shared', label: '공유 상가', hint: '부고를 1회 이상 공유한 상가만 분모' },
  { key: 'all', label: '발인 상가', hint: '공유하지 않은 상가까지 분모' },
];

export const AdminPenetrationSection: FC<{
  kind: OrderStatKind;
  range: { from: string; to: string };
  /** 상조 필터 (비어 있으면 전체) */
  brandKeys: string[];
}> = function AdminPenetrationSection({ kind, range, brandKeys }) {
  const [base, setBase] = useState<PenetrationBase>('shared');
  const [chart, setChart] = useState<'calendar' | 'trend'>('calendar');
  const [heatBy, setHeatBy] = useState<'rate' | 'value'>('rate');
  const [drill, setDrill] = useState<{
    drill: PenetrationDrillDown;
    title: string;
  } | null>(null);

  const {
    status,
    fevents,
    orders,
    truncated,
    errorMessage,
    orderWindow,
    feventFetchLimit,
    resolveBrandName,
    refetch,
  } = useAdminPenetration(kind, range.from, range.to);

  const { rows, orphanOrderCount } = useMemo(
    () => buildPenetrationRows(fevents, orders, kind, { brandKeys }, resolveBrandName),
    [fevents, orders, kind, brandKeys, resolveBrandName],
  );

  const result = useMemo(
    () => aggregatePenetration(rows, orphanOrderCount, range),
    [rows, orphanOrderCount, range],
  );

  const totals = result.totals;
  const sharedBase = base === 'shared';
  const baseLabel = sharedBase ? '공유 상가' : '발인 상가';
  const denomTotal = sharedBase ? totals.sharedFeventCount : totals.feventCount;
  const denomBuying = sharedBase
    ? totals.sharedBuyingFeventCount
    : totals.buyingFeventCount;
  //* 금액·건수도 분모와 같은 모수에서 — 공유 분모일 땐 공유 상가 주문만 (드릴다운 합계와 일치)
  const denomMetrics = metricsOf(totals, base);

  //* 기간 전체 구매율 = 하루하루를 견줄 기준선. 이게 100%다
  const baseline = denomTotal > 0 ? denomBuying / denomTotal : null;

  //* 막대·칸 전체 = 그날의 모수 상가, 안쪽 진한 부분 = 그중 구매한 상가
  const dayPoints = useMemo(
    () =>
      result.days.map((day) => ({
        date: day.date,
        value: sharedBase ? day.sharedFeventCount : day.feventCount,
        subValue: sharedBase ? day.sharedBuyingFeventCount : day.buyingFeventCount,
        note: (() => {
          const amount = sharedBase ? day.sharedAmount : day.amount;
          return amount > 0 ? `${compactWon(Math.round(amount))}원` : undefined;
        })(),
      })),
    [result.days, sharedBase],
  );
  const dayLabels = useMemo(
    () => ({ value: baseLabel, unit: '건', sub: '구매' }),
    [baseLabel],
  );

  const drillRows = useMemo(
    () => (drill ? drillDownPenetrationRows(rows, drill.drill) : []),
    [drill, rows],
  );

  /** 목록도 화면과 같은 모수로 (공유 기준이면 공유한 상가만) */
  const openDrill = (
    next: Omit<PenetrationDrillDown, 'sharedOnly'>,
    title: string,
  ) => setDrill({ drill: { ...next, sharedOnly: sharedBase }, title });

  const kindLabel = KIND_LABEL[kind];
  const { lead, tail } = ORDER_WINDOW_DAYS[kind];

  return (
    <div className="space-y-3">
      {status === 'loading' && <CenterLoading />}
      {status === 'error' && (
        <ErrorBox message={errorMessage ?? '오류가 발생했습니다.'} onRetry={refetch} />
      )}

      {status === 'success' && (
        <>
          {truncated && (
            <NoticeBox tone="warn">
              조회 상한({feventFetchLimit.toLocaleString()}건)에 걸려 일부만 집계했습니다.
              구매율이 실제보다 낮게 보일 수 있으니 기간을 좁혀주세요.
            </NoticeBox>
          )}

          {totals.feventCount === 0 ? (
            <EmptyBox message="해당 기간에 발인한 상가가 없습니다." />
          ) : (
            <>
              {/* 분모 — 이 화면의 모든 %가 여기 따라 바뀐다 */}
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-white p-3 shadow-smd">
                <span className="shrink-0 text-13 font-medium text-gray-600">
                  구매율 분모
                </span>
                {BASE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setBase(option.key)}
                    title={option.hint}
                    className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                      base === option.key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                <span className="text-12 text-gray-500">
                  {sharedBase
                    ? '부고를 보낸 상가만 셉니다 — 안 보낸 상가엔 주문이 들어올 길이 없습니다.'
                    : '부고를 안 보낸 상가까지 분모에 넣습니다.'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCard
                  label={baseLabel}
                  value={denomTotal.toLocaleString()}
                  sub={
                    sharedBase
                      ? `발인 ${totals.feventCount.toLocaleString()}상가 중`
                      : `공유 ${totals.sharedFeventCount.toLocaleString()}상가 포함`
                  }
                />
                <StatCard
                  label={`${kindLabel} 구매율`}
                  value={formatRate(denomBuying, denomTotal)}
                  sub={`${denomBuying.toLocaleString()} / ${denomTotal.toLocaleString()}상가`}
                  accent="indigo"
                />
                <StatCard
                  label="미구매"
                  value={(denomTotal - denomBuying).toLocaleString()}
                  sub={`${baseLabel} 중 안 산 곳`}
                  accent="orange"
                />
                <StatCard
                  label="총 금액"
                  value={`${formatPrice(Math.round(denomMetrics.amount))}원`}
                  sub={`주문 ${denomMetrics.orderCount.toLocaleString()}건${
                    kind === 'gift'
                      ? ` · 수량 ${denomMetrics.quantity.toLocaleString()}`
                      : ''
                  }`}
                />

                <StatCard
                  label="구매단가"
                  value={`${
                    denomMetrics.orderCount > 0
                      ? formatPrice(
                          Math.round(denomMetrics.amount / denomMetrics.orderCount),
                        )
                      : '-'
                  }원`}
                  sub="주문 한 건당"
                />
                <StatCard
                  label="구매상가당"
                  value={`${
                    denomBuying > 0
                      ? formatPrice(Math.round(denomMetrics.amount / denomBuying))
                      : '-'
                  }원`}
                  sub={`산 상가 ${denomBuying.toLocaleString()}곳 기준`}
                />
                <StatCard
                  label={`${baseLabel}당`}
                  value={`${
                    denomTotal > 0
                      ? formatPrice(Math.round(denomMetrics.amount / denomTotal))
                      : '-'
                  }원`}
                  sub="안 산 곳까지 포함"
                />
                <StatCard
                  label="상가당 건수"
                  value={
                    denomBuying > 0
                      ? (denomMetrics.orderCount / denomBuying).toFixed(1)
                      : '-'
                  }
                  sub="산 상가에서 나온 주문 수"
                />
              </div>

              <NoticeBox>
                {`${baseLabel} 중 ${kindLabel} 주문이 1건 이상 붙은 곳을 구매로 셉니다. 기간 전체 구매율 ${formatRate(
                  denomBuying,
                  denomTotal,
                )}가 '평균 대비 100%'의 기준선이고, 하루하루는 이 기준선과 견줍니다. 주문은 발인일 앞 ${lead}일 ~ 뒤 ${tail}일(${orderWindow.from} ~ ${orderWindow.to})까지 훑어 상가에 붙입니다`}
                {kind === 'gift'
                  ? ' — 답례품은 발인 뒤에도 주문되기 때문입니다. 전액 취소된 주문은 구매로 세지 않습니다.'
                  : ' — 화환은 조문 시점에 결제되기 때문입니다.'}
              </NoticeBox>

              {orphanOrderCount > 0 && (
                <NoticeBox tone="warn">
                  상가 정보가 없는 주문 {orphanOrderCount.toLocaleString()}건은 어느 상가
                  몫인지 알 수 없어 구매율에서 빠졌습니다.
                </NoticeBox>
              )}

              <div className="flex flex-wrap items-center gap-1.5">
                {CHART_VIEWS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setChart(item.key)}
                    className={`rounded-lg px-4 py-1.5 text-14 font-medium ${
                      chart === item.key
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}

                {chart === 'calendar' && (
                  //* 히트맵 기준 — 상가가 많은 날과 잘 판 날은 다른 날이다
                  <span className="ml-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-13 text-gray-500">색 기준</span>
                    {HEAT_METRICS.map((metric) => (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => setHeatBy(metric.key)}
                        title={metric.hint}
                        className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                          heatBy === metric.key
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {metric.label}
                      </button>
                    ))}
                  </span>
                )}

                <button
                  type="button"
                  onClick={() => openDrill({ buying: 'idle' }, '미구매 상가')}
                  className="rounded-lg bg-white px-3 py-1.5 text-14 font-medium text-gray-700 hover:bg-gray-100"
                >
                  미구매 상가
                </button>
                <button
                  type="button"
                  onClick={() => openDrill({}, '전체 상가')}
                  className="ml-auto rounded-lg bg-white px-3 py-1.5 text-14 font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  전체 목록 보기
                </button>
              </div>

              {chart === 'calendar' ? (
                <AnalyticsCalendar
                  days={dayPoints}
                  labels={dayLabels}
                  showRate
                  heatBy={heatBy}
                  rateBaseline={baseline}
                  hint={`칸의 %는 그날 ${baseLabel} 중 ${kindLabel}을 산 비율, 아래 줄은 기간 평균(${formatRate(
                    denomBuying,
                    denomTotal,
                  )})을 100%로 본 지수입니다. 날짜를 누르면 그날 상가 목록이 열립니다.`}
                  onDayClick={(date) => openDrill({ date }, `${date} 발인`)}
                  onWeekClick={(from, to, label) =>
                    openDrill({ dateFrom: from, dateTo: to }, label)
                  }
                />
              ) : (
                <AnalyticsDailyTrend
                  title={`발인일별 ${baseLabel} · ${kindLabel} 구매`}
                  days={dayPoints}
                  labels={dayLabels}
                />
              )}

              <AdminPenetrationTable
                kind={kind}
                base={base}
                result={result}
                onExport={() => exportPenetrationExcel(kind, base, result, rows, range)}
                onBrandClick={(brandKey, brandName) =>
                  openDrill({ brandKey }, brandName)
                }
              />
            </>
          )}
        </>
      )}

      {drill && (
        <AdminPenetrationFeventModal
          kind={kind}
          title={drill.title}
          subtitle={`발인 ${range.from} ~ ${range.to}${
            sharedBase ? ' · 공유 상가만' : ''
          }`}
          rows={drillRows}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
